import {
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { WhitePageContainer } from '../PersonalOrders';
import {
  AllinedPageContainer,
  InsidePageContainer,
  RedLinkButton,
  TicketBoughtCard,
  parseDevelonProducts,
} from '../common';
import { useState } from 'react';
import { LoaderCountContext } from 'context/LoaderV5';
import { useNetwork } from 'services/network';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ModeEditOutlineOutlined } from '@mui/icons-material';
import { useTicketsSelector, useTicketsStore } from '../store/ticketsStore';
import EditTicketDatesDialog from '../common/EditTicketDatesDialog';
import {
  dataLayerPagesEnum,
  setVirtualPageDataLayer,
} from 'utils/dataLayerManager';

const PersonalTickets = ({ loggedUser, locale }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Tickets(intl).myTickets;
  const i18nCommon = translations.TicketsV2(intl).Common;
  const i18nV2 = translations.TicketsV2(intl).MyTickets;
  const { isLoaderActive, setLoaderActive } = useContext(LoaderCountContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  const network = useNetwork();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { ticketToEditDates } = useTicketsSelector();

  // States
  const [tickets, setTickets] = useState();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState({});

  // Effects
  useEffect(() => {
    const fetchUserTickets = async () => {
      showLoader();
      const data = await network.azureLambda({
        params: `/account/tickets?email=${loggedUser.email.replace(
          '+',
          '%2b'
        )}`,
        customMethod: 'get',
        data: {},
        federationId: loggedUser?.salId,
      });

      if (data && data.length) {
        const ticketIds = data.map((t) => t.ticket_code);
        const develonTickets = await network.aggregatePersonalTicketsLambda({
          federationId: loggedUser?.salId,
          ids: ticketIds,
        });
        const ticketWithEmptySlots = parseDevelonProducts({
          tickets: develonTickets.filter((dt) => dt.id),
          loggedUser,
          locale,
          needUnparsedTicket: true,
        });
        const isLoadingTickets = {};
        ticketWithEmptySlots.map((t) => (isLoadingTickets[t.id] = false));
        setIsLoading(isLoadingTickets);
        setTickets(ticketWithEmptySlots);
      }
      hideLoader();
    };
    if (loggedUser && !tickets) fetchUserTickets();
  }, [loggedUser]);

  const downloadPDF = async (pdf, fileName) => {
    const source = `data:application/pdf;base64,${pdf}`;
    const link = document.createElement('a');
    link.href = source;
    link.download = `${fileName}.pdf`;
    return link.click();
  };

  const onDownloadTicket = async (ticket) => {
    showLoader();
    try {
      const pdf = await network.ticketPdfLambda({
        ticketId: ticket.id,
        scope: 'tickets',
        federationId: loggedUser?.salId,
      });
      if (pdf) {
        const fileName = ticket.isMerch
          ? `${ticket.description.split(' ').join('_')}`
          : `${ticket.owner}_ticket`;

        downloadPDF(pdf.body, fileName);
      }
    } catch (error) {
      console.log(error);
    }
    hideLoader();
  };

  const getTicketsAvailableDates = async (ticket) => {
    setIsLoading((prev) => ({ ...prev, [ticket.id]: true }));
    const startDate = `${new Date().getFullYear()}-01-01`;
    const endDate = `${new Date().getFullYear()}-12-31`;
    const res = await network.ticketingLambda({
      params: `/calendars/availability?area_code=${ticket.product_snap.area_code}&pax_number=1&start_date=${startDate}&end_date=${endDate}`,
      customMethod: 'get',
      data: {},
      federationId: loggedUser?.salId,
    });
    setIsLoading((prev) => ({ ...prev, [ticket.id]: false }));
    return !res.error
      ? res.slots.map((s) => ({
          date: s.date,
          groupCodes: s.groups.map((g) => g.code),
          areaCode: res.code,
        }))
      : [];
  };

  const onEditTicketDate = async (ticket) => {
    const slotsAvailableForTickets = await getTicketsAvailableDates(
      ticket.unparsedTicket
    );

    const ticketsWithSlots = {
      ...ticket,
      unparsedTicket: {
        ...ticket.unparsedTicket,
        slots: slotsAvailableForTickets,
      },
    };
    useTicketsStore.setState({ ticketToEditDates: ticketsWithSlots });
    setIsDialogOpen(true);
  };

  const closeDialog = () => setIsDialogOpen(false);

  const onDateChange = async (dates) => {
    const new_reservation_details = dates
      .filter((d) => !!d.selected)
      .map((date) => ({
        reservation_date: date.date,
        area_code: date.areaCode,
        group_code: date.groupCodes[0],
      }));
    const tickets = [ticketToEditDates.id];
    const oldReservation = ticketToEditDates.unparsedTicket.reservation_snap;
    const old_reservation_number = oldReservation.number;
    const dates_to_remove = oldReservation.reservation_dates.map((d) => ({
      group_id: d.group_id,
      reservation_start_date_time: d.reservation_start_date_time,
    }));
    const input = {
      params: '/reservations/move',
      customMethod: 'post',
      data: {
        new_reservation_details,
        dates_to_remove,
        tickets,
        old_reservation_number,
      },
    };
    const res = await network.ticketingLambda({
      params: '/reservations/move',
      customMethod: 'post',
      data: input,
      federationId: loggedUser?.salId,
    });
    setVirtualPageDataLayer(dataLayerPagesEnum.MY_TICKETS_EDIT);
    if (res.status === 'confirmed') {
      location.reload();
    }
    hideLoader();
  };

  const renderActionContainer = (ticket) => (
    <Stack
      flexDirection={'row'}
      justifyContent={isMdDown ? 'flex-start' : 'flex-end'}
      alignItems={'center'}
      gap={'30px'}
      flexWrap={'wrap'}
      paddingLeft={isMdDown ? '0px' : '5px'}
    >
      {!ticket.isMerch ? (
        <>
          {isLoading[ticket.id] ? (
            <Stack
              justifyContent={'center'}
              alignItems={'center'}
              color={theme.palette.primary.main}
              style={{ minWidth: '170px' }}
            >
              <CircularProgress color={'inherit'} />
            </Stack>
          ) : (
            <RedLinkButton
              onClick={() => onEditTicketDate(ticket)}
              msg={i18nCommon.editDate}
              icon={
                <ModeEditOutlineOutlined fontSize="small" color="primary" />
              }
            />
          )}
        </>
      ) : null}
      <RedLinkButton
        onClick={() => onDownloadTicket(ticket)}
        msg={i18nCommon.download[ticket.isMerch ? 'merch' : 'ticket']}
        icon={<ExpandMoreIcon fontSize="small" color="primary" />}
      />
    </Stack>
  );

  return (
    <Stack>
      {/**
       * MAYBE ON DRUPAL ???
       */}
      <WhitePageContainer>
        <InsidePageContainer>
          <Typography
            variant={isMdDown ? 's' : 'm'}
            textTransform={'uppercase'}
            textAlign={'center'}
          >
            {i18n.subtitle}
          </Typography>
          <Typography variant={'xxl'} textAlign={'center'}>
            {i18nV2.title}
          </Typography>
          <Typography
            variant="body1"
            textAlign={'center'}
            sx={{ marginTop: '20px' }}
          >
            Sono presenti in questa sezione tutti i biglietti intestati.
          </Typography>
        </InsidePageContainer>
      </WhitePageContainer>
      {/**
       * MAYBE ON DRUPAL ???
       */}
      {isLoaderActive && !tickets?.length ? null : (
        <AllinedPageContainer>
          <InsidePageContainer>
            <Stack direction={'column'} gap={isMdDown ? '20px' : '25px'}>
              {tickets && tickets.length
                ? tickets
                    .sort((a, b) => {
                      const firstDate =
                        a.dates[0].reservation_start_date_time.replace(
                          ' ',
                          'T'
                        ) || '';
                      const secondDate =
                        b.dates[0].reservation_start_date_time.replace(
                          ' ',
                          'T'
                        ) || '';
                      return new Date(secondDate) - new Date(firstDate);
                    })
                    .map((ticket, idx) => (
                      <TicketBoughtCard
                        key={idx}
                        ticket={ticket}
                        locale={locale}
                        actionsContainer={renderActionContainer(ticket)}
                      />
                    ))
                : null}
            </Stack>
          </InsidePageContainer>
        </AllinedPageContainer>
      )}
      <EditTicketDatesDialog
        isOpen={isDialogOpen}
        onClose={() => closeDialog()}
        onAccept={onDateChange}
        loggedUser={loggedUser}
        locale={locale}
      />
    </Stack>
  );
};
export default PersonalTickets;
